<template>
  <AiEducationLayout :menu-items="menuItems">
    <EducationPageHeader title="学习计划" description="按当前学生权限查看并完成学习计划" />
    <ContentWrap>
      <EducationState v-if="state === 'loading'" state="loading" title="正在加载学习计划" />
      <EducationState
        v-else-if="state === 'error'"
        state="error"
        title="学习计划加载失败"
        @retry="load"
      />
      <EducationState
        v-else-if="!plans.length"
        state="empty"
        title="暂无学习计划"
        description="教师或系统生成学习计划后，会显示在这里。"
      />
      <el-collapse v-else v-model="expanded" accordion @change="loadItems">
        <el-collapse-item v-for="plan in plans" :key="plan.id" :name="String(plan.id)">
          <template #title>
            <span class="plan-title">{{ plan.title || '未命名计划' }}</span>
            <el-tag v-if="plan.status != null" size="small">{{
              plan.status === 20 ? '进行中' : '已完成'
            }}</el-tag>
          </template>
          <p class="plan-description">{{ plan.description || '暂无计划说明' }}</p>
          <EducationState v-if="itemsLoading" state="loading" title="正在加载计划任务" />
          <ul v-else-if="items.length" class="plan-items">
            <li v-for="item in items" :key="item.id">
              <span>{{ item.title || item.description || '学习任务' }}</span>
              <el-tag :type="item.status === 20 ? 'success' : 'info'" size="small">
                {{ item.status === 20 ? '已完成' : '待完成' }}
              </el-tag>
            </li>
          </ul>
          <EducationState v-else state="empty" title="暂无计划任务" />
        </el-collapse-item>
      </el-collapse>
    </ContentWrap>
  </AiEducationLayout>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import AiEducationLayout from '../layout/AiEducationLayout.vue'
import type { EducationMenuItem } from '../layout/types'
import EducationPageHeader from '../components/EducationPageHeader.vue'
import EducationState from '../components/EducationState.vue'
import {
  getMyStudentLearningPlans,
  getStudentLearningPlanItems,
  type StudentLearningPlanItemVO,
  type StudentLearningPlanVO
} from '@/api/aiEducation'

defineOptions({ name: 'AiEducationStudentLearningPlans' })
const plans = ref<StudentLearningPlanVO[]>([])
const items = ref<StudentLearningPlanItemVO[]>([])
const expanded = ref('')
const state = ref<'loading' | 'ready' | 'error'>('loading')
const itemsLoading = ref(false)
const menuItems: EducationMenuItem[] = [
  {
    title: '学习首页',
    path: '/ai-Application-center/ai-education/student/home',
    icon: 'ep:home-filled'
  },
  {
    title: '我的课程',
    path: '/ai-Application-center/ai-education/student/courses',
    icon: 'ep:reading'
  },
  {
    title: '我的作业',
    path: '/ai-Application-center/ai-education/student/assignments',
    icon: 'ep:document-checked'
  },
  {
    title: '学习计划',
    path: '/ai-Application-center/ai-education/student/learning-plans',
    icon: 'ep:calendar'
  },
  {
    title: 'AI 学伴',
    path: '/ai-Application-center/ai-education/student/assistant',
    icon: 'ep:magic-stick'
  }
]
const load = async () => {
  state.value = 'loading'
  try {
    plans.value = (await getMyStudentLearningPlans()) || []
    state.value = 'ready'
  } catch {
    state.value = 'error'
  }
}
const loadItems = async (name: string | number) => {
  if (!name) return
  itemsLoading.value = true
  items.value = []
  try {
    items.value = (await getStudentLearningPlanItems(Number(name))) || []
  } finally {
    itemsLoading.value = false
  }
}
onMounted(load)
</script>

<style scoped>
.plan-title {
  margin-right: 12px;
  font-weight: 600;
}
.plan-description {
  color: #697681;
}
.plan-items {
  margin: 0;
  padding: 0;
  list-style: none;
}
.plan-items li {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #edf0f2;
}
</style>
